import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import baseURL from '../url';
import './Cart.css';
import whatsappIcon from '../../images/wpp.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Link as Anchor } from "react-router-dom";
import moneda from '../moneda';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useCatalogData } from '../CatalogData/CatalogDataProvider';
import { buildWhatsAppUrl } from '../../utils/whatsapp';

const noop = () => {};
const CART_STORAGE_KEY = 'cart';

const leerEntradasCarrito = () => {
    try {
        const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
        return Array.isArray(cart) ? cart : [];
    } catch (error) {
        return [];
    }
};

const guardarEntradasCarrito = (entries) => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(entries));
};

const esEntradaObjeto = (entry) => entry && typeof entry === 'object' && !Array.isArray(entry);

const tieneIdProductoValido = (idProducto) => {
    const idNormalizado = String(idProducto ?? '').trim();
    return /^\d+$/.test(idNormalizado) && Number(idNormalizado) > 0;
};

const normalizarCantidad = (cantidad) => {
    const cantidadNumerica = Number(cantidad);
    return Number.isInteger(cantidadNumerica) && cantidadNumerica > 0
        ? cantidadNumerica
        : 1;
};

const tieneCantidadValida = (cantidad) => {
    const cantidadNumerica = Number(cantidad);
    return Number.isInteger(cantidadNumerica) && cantidadNumerica > 0;
};

const normalizarItems = (entry) => {
    if (Array.isArray(entry?.items)) {
        return entry.items;
    }

    if (Array.isArray(entry?.item)) {
        return entry.item;
    }

    return [];
};

const obtenerValorGuardado = (key) => {
    try {
        return localStorage.getItem(key) || '';
    } catch (error) {
        return '';
    }
};

const obtenerConsultaUrlSegura = (value) => {
    if (typeof value !== 'string' || !value.trim()) {
        return '';
    }

    const consultaUrl = value.trim();

    try {
        const baseUrl = typeof window !== 'undefined'
            ? window.location.origin
            : 'https://localhost';
        const parsedUrl = new URL(consultaUrl, baseUrl);

        return ['http:', 'https:'].includes(parsedUrl.protocol)
            ? consultaUrl
            : '';
    } catch (error) {
        return '';
    }
};

const obtenerPedidoConfirmado = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return null;
    }

    const idPedidoValue = typeof data.idPedido === 'string'
        && /^\d+$/.test(data.idPedido.trim())
        ? Number(data.idPedido.trim())
        : data.idPedido;
    const idPedido = typeof idPedidoValue === 'number'
        ? idPedidoValue
        : Number.NaN;
    const tokenConsulta = typeof data.tokenConsulta === 'string'
        ? data.tokenConsulta.trim()
        : '';
    const consultaUrl = obtenerConsultaUrlSegura(data.consultaUrl);

    if (
        !Number.isSafeInteger(idPedido)
        || idPedido <= 0
        || !tokenConsulta
        || !consultaUrl
        || !Array.isArray(data.productos)
    ) {
        return null;
    }

    return {
        ...data,
        idPedido,
        tokenConsulta,
        consultaUrl,
    };
};

export default function Cart({
    isOpen = false,
    onAfterClose = noop,
    onCartCountChange = noop,
    onRequestClose = noop,
}) {
    const {
        productos,
        tiendaPrincipal,
        status: catalogStatus,
        ensureProductos,
        ensureTienda,
        retryProductos,
        retryTienda,
    } = useCatalogData();
    const [cartEntries, setCartEntries] = useState(leerEntradasCarrito);
    const [modalIsOpen2, setModalIsOpen2] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [name, setName] = useState('');
    const [telefono, setTelefono] = useState('');
    const [codigo, setCodigo] = useState('');
    const [descuento, setDescuento] = useState(0);
    const [codigoValido, setCodigoValido] = useState(false);
    const [codigoMensaje, setCodigoMensaje] = useState('');
    const [validandoCodigo, setValidandoCodigo] = useState(false);
    const [deliveryOption, setDeliveryOption] = useState('delivery');
    const [metodos, setMetodos] = useState([]);
    const [metodosStatus, setMetodosStatus] = useState('idle');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [pagoRecibir, setPagoRecibir] = useState('');
    const [estado, setEstado] = useState(null);
    const [consultaUrl, setConsultaUrl] = useState(() => obtenerValorGuardado('consultaUrl'));
    const productsAttemptedForOpenRef = useRef(false);
    const metodosRequestRef = useRef(null);
    let now = new Date();
    // Obtener la diferencia de zona horaria en minutos ((Colombia es GMT-5)
    let offset = -5 * 60; // Colombia  está a GMT-3
    // Ajustar la fecha actual sumando/restando el offset
    let colombiaTime = new Date(now.getTime() + offset * 60 * 1000);
    // Formatear la fecha a 'YYYY-MM-DD HH:MM:SS'
    let createdAt = colombiaTime.toISOString().slice(0, 19).replace('T', ' ');

    const [address, setAddress] = useState('');
    const [formErrors, setFormErrors] = useState({});

    const cartItems = useMemo(() => cartEntries.map((storedEntry, cartIndex) => {
        const entry = esEntradaObjeto(storedEntry) ? storedEntry : {};
        const idProductoValido = tieneIdProductoValido(entry.idProducto);
        const producto = idProductoValido
            ? productos.find(candidate => String(candidate.idProducto) === String(entry.idProducto))
            : null;

        return {
            ...entry,
            ...(producto || {}),
            idProducto: entry.idProducto,
            cantidad: normalizarCantidad(entry.cantidad),
            cantidadPersistidaValida: tieneCantidadValida(entry.cantidad),
            items: normalizarItems(entry),
            cartIndex,
            productoResuelto: Boolean(producto),
            idProductoValido,
            titulo: producto?.titulo || entry.titulo || 'Producto no disponible',
        };
    }), [cartEntries, productos]);

    const totalPrice = useMemo(() => cartItems.reduce((total, item) => {
        const precio = Number(item.precio);
        return Number.isFinite(precio) ? total + precio * item.cantidad : total;
    }, 0), [cartItems]);

    const hasInvalidIdItems = cartItems.some(item => !item.idProductoValido);
    const hasUnavailableItems = catalogStatus.productos === 'loaded'
        && cartItems.some(item => item.idProductoValido && !item.productoResuelto);
    const hasUnresolvedItems = hasInvalidIdItems || hasUnavailableItems;
    const loadingProductos = cartEntries.length > 0
        && (catalogStatus.productos === 'idle' || catalogStatus.productos === 'loading');

    const releerCarrito = useCallback(() => {
        setCartEntries(leerEntradasCarrito());
    }, []);

    useEffect(() => {
        setCodigoValido(false);
        setDescuento(0);
        setCodigoMensaje('');
    }, [cartEntries]);

    useEffect(() => {
        if (!isOpen || cartEntries.length === 0) {
            productsAttemptedForOpenRef.current = false;
            return;
        }

        if (productsAttemptedForOpenRef.current) {
            return;
        }

        if (catalogStatus.productos === 'idle') {
            productsAttemptedForOpenRef.current = true;
            ensureProductos().catch(() => {});
        } else if (catalogStatus.productos === 'error') {
            productsAttemptedForOpenRef.current = true;
            retryProductos().catch(() => {});
        }
    }, [
        cartEntries.length,
        catalogStatus.productos,
        ensureProductos,
        isOpen,
        retryProductos,
    ]);

    useEffect(() => {
        const fetchEstado = async () => {
            try {
                const response = await fetch(`${baseURL}/estado.php?`);
                const data = await response.json();
                if (data.error) {
                    console.error('No se pudo obtener el estado de la tienda');
                } else {
                    setEstado(data.estado);
                }
            } catch (error) {
                console.error("Error al obtener el estado:", error);
            }
        };

        fetchEstado();
    }, []);
    useEffect(() => {
        window.addEventListener('inksu:cart-updated', releerCarrito);

        return () => {
            window.removeEventListener('inksu:cart-updated', releerCarrito);
        };
    }, [releerCarrito]);

    useEffect(() => {
        onCartCountChange(cartEntries.length);
    }, [cartEntries.length, onCartCountChange]);



    const cargarMetodos = () => {
        if (metodosRequestRef.current) {
            return metodosRequestRef.current;
        }

        const previousPaymentMethod = paymentMethod;
        const previousPagoRecibir = pagoRecibir;

        setMetodos([]);
        setPaymentMethod('');
        setPagoRecibir('');
        setMetodosStatus('loading');

        let request;
        request = (async () => {
            try {
                const response = await fetch(`${baseURL}/metodoGet.php`, {
                    method: 'GET',
                });

                if (!response?.ok) {
                    throw new Error('No fue posible cargar los métodos de pago.');
                }

                const data = await response.json();
                if (!Array.isArray(data?.metodos)) {
                    throw new Error('No fue posible cargar los métodos de pago.');
                }

                const metodosActivos = data.metodos.filter(metodo => metodo?.estado === 'Activo');
                const previousMethodIsStillActive = metodosActivos.some(
                    metodo => metodo.tipo === previousPaymentMethod
                );
                const nextPaymentMethod = previousMethodIsStillActive
                    ? previousPaymentMethod
                    : metodosActivos[0]?.tipo || '';

                setMetodos(metodosActivos);
                setPaymentMethod(nextPaymentMethod);
                setPagoRecibir(
                    nextPaymentMethod === 'Transferencia'
                        ? (
                            previousPaymentMethod === 'Transferencia'
                            && ['Si', 'No'].includes(previousPagoRecibir)
                                ? previousPagoRecibir
                                : 'Si'
                        )
                        : ''
                );
                setMetodosStatus('loaded');
                return metodosActivos;
            } catch (error) {
                setMetodos([]);
                setPaymentMethod('');
                setPagoRecibir('');
                setMetodosStatus('error');
                throw error;
            } finally {
                if (metodosRequestRef.current === request) {
                    metodosRequestRef.current = null;
                }
            }
        })();

        metodosRequestRef.current = request;
        return request;
    };

    const obtenerImagen = (item) => {
        return item.imagen1 || item.imagen2 || item.imagen3 || item.imagen4 || null;
    };

    const closeModal = () => {
        onRequestClose();
    };

    const openModal2 = () => {
        setModalIsOpen2(true);
        cargarMetodos().catch(() => {});

        if (catalogStatus.tienda === 'idle') {
            ensureTienda().catch(() => {});
        } else if (catalogStatus.tienda === 'error') {
            retryTienda().catch(() => {});
        }
    };

    const closeModal2 = () => {
        setModalIsOpen2(false);
    };

    const removeFromCart = (index) => {
        const updatedCart = cartEntries.filter((_, entryIndex) => entryIndex !== index);
        setCartEntries(updatedCart);
        guardarEntradasCarrito(updatedCart);
    };

    const clearCart = () => {
        setCartEntries([]);
        localStorage.removeItem(CART_STORAGE_KEY);
    };

    const resetCodigoValidacion = () => {
        setCodigoValido(false);
        setDescuento(0);
        setCodigoMensaje('');
    };

    const validarCodigoPublico = async () => {
        const codigoLimpio = codigo.trim();

        if (!codigoLimpio) {
            resetCodigoValidacion();
            return;
        }

        const productosCodigo = cartItems
            ?.filter(item => (
                item.idProductoValido
                && item.productoResuelto
                && item.cantidadPersistidaValida
            ))
            ?.map(item => ({
                idProducto: item.idProducto,
                cantidad: item.cantidad
            }));

        if (!productosCodigo || productosCodigo.length === 0) {
            setCodigoValido(false);
            setDescuento(0);
            setCodigoMensaje('Agrega productos al carrito para validar el codigo.');
            return;
        }

        const formData = new FormData();
        formData.append('codigo', codigoLimpio);
        formData.append('productos', JSON.stringify(productosCodigo));

        setValidandoCodigo(true);

        try {
            const response = await fetch(`${baseURL}/codigoPublicValidate.php`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data?.valido) {
                const descuentoCalculado = Number(data.descuentoCalculado) || 0;
                setCodigoValido(true);
                setDescuento(descuentoCalculado);
                setCodigoMensaje(data.mensaje || 'Codigo aplicado correctamente.');
            } else {
                setCodigoValido(false);
                setDescuento(0);
                setCodigoMensaje(data?.mensaje || data?.error || 'Este codigo no aplica al carrito.');
            }
        } catch (error) {
            console.error('Error al validar codigo:', error);
            setCodigoValido(false);
            setDescuento(0);
            setCodigoMensaje('No pudimos validar el codigo. Puedes continuar y el servidor lo revisara al crear el pedido.');
        } finally {
            setValidandoCodigo(false);
        }
    };
    const handleWhatsappMessage = (data) => {
        const { idPedido, nombre, telefono, entrega, pago, codigo, total, nota, productos, pagoRecibir, consultaUrl } = data;
        const productosConfirmados = Array.isArray(productos) ? productos : [];
        const formattedTotalPrice = total?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

        // Formatear los detalles de los productos
        const productosDetails = productosConfirmados.map(item => {
            return `\n✅ *${item.titulo}* \n      Precio: ${moneda} ${item?.precio?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}  x  ${item.cantidad}\n      ${item.items}\n`;
        }).join('');

        const seguimiento = consultaUrl ? `\n\nSeguimiento de pedido: ${consultaUrl}` : '';
        const message = `¡Hola! 🌟 Mi pedido es el N°${idPedido}\n${productosDetails}\n👤 Nombre: ${nombre}\n\n📱 Teléfono: ${telefono}\n\n📦 Entrega: ${entrega}\n\n💵 Forma de pago: ${pago}\n\n📌 Pago al recibirlo: ${pagoRecibir}\n\n🏷 Código de descuento: ${codigo}\n\n✅ Nota: ${nota}\n\n*Total: ${moneda} ${formattedTotalPrice}*${seguimiento}`;

        const whatsappUrl = buildWhatsAppUrl(tiendaPrincipal?.telefono, message);
        if (!whatsappUrl) {
            return false;
        }

        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

        return true;
    };





    // Función para aumentar la cantidad de un producto en el carrito
    const increaseQuantity = (index) => {
        const item = cartItems[index];
        if (!item) {
            return;
        }

        const stockDisponible = Number(item.stock);
        const cantidadActual = Number(item.cantidad) || 0;

        if (!Number.isInteger(stockDisponible) || stockDisponible < 1) {
            toast.error('Este producto no tiene stock disponible.', { autoClose: 1200 });
            return;
        }

        if (cantidadActual >= stockDisponible) {
            toast.error(`Solo hay ${stockDisponible} unidades disponibles.`, { autoClose: 1200 });
            return;
        }

        const updatedCartEntries = [...cartEntries];
        const storedEntry = esEntradaObjeto(updatedCartEntries[index])
            ? updatedCartEntries[index]
            : {};
        updatedCartEntries[index] = {
            ...storedEntry,
            cantidad: cantidadActual + 1
        };
        setCartEntries(updatedCartEntries);
        guardarEntradasCarrito(updatedCartEntries);
    };

    // Función para disminuir la cantidad de un producto en el carrito
    const decreaseQuantity = (index) => {
        const item = cartItems[index];
        if (!item || item.cantidad <= 1) {
            return;
        }

        const updatedCartEntries = [...cartEntries];
        const storedEntry = esEntradaObjeto(updatedCartEntries[index])
            ? updatedCartEntries[index]
            : {};
        updatedCartEntries[index] = {
            ...storedEntry,
            cantidad: item.cantidad - 1,
        };
        setCartEntries(updatedCartEntries);
        guardarEntradasCarrito(updatedCartEntries);
    };

    const retryCatalogProducts = () => {
        retryProductos().catch(() => {});
    };

    const retryCatalogStore = () => {
        retryTienda().catch(() => {});
    };

    const retryPaymentMethods = () => {
        cargarMetodos().catch(() => {});
    };

    const isCheckoutItemValid = (item) => (
        item.idProductoValido
        && item.productoResuelto
        && item.cantidadPersistidaValida
    );

    const getUnavailableProductMessage = (item) => {
        if (!item.idProductoValido) {
            return 'Esta entrada no tiene un producto válido. Puedes retirarla del carrito.';
        }

        if (catalogStatus.productos === 'error') {
            return 'No pudimos comprobar este producto. Reintenta la carga del catálogo.';
        }

        return 'Este producto ya no está disponible en el catálogo. Puedes retirarlo del carrito.';
    };


    const clearFormError = (field) => {
        setFormErrors(currentErrors => {
            if (!currentErrors[field]) {
                return currentErrors;
            }

            const nextErrors = { ...currentErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    const selectDeliveryOption = (option) => {
        setDeliveryOption(option);
        clearFormError('deliveryOption');

        if (option !== 'delivery') {
            clearFormError('address');
        }
    };

    const validateCheckout = () => {
        const errors = {};
        const telefonoNormalizado = telefono.replace(/\D/g, '');
        const deliveryOptions = ['delivery', 'pickup', 'digital'];

        if (cartItems.length === 0) {
            errors.cart = 'Agrega al menos un producto antes de finalizar.';
        } else if (catalogStatus.productos !== 'loaded') {
            errors.cart = 'Actualiza la información de los productos antes de finalizar el pedido.';
        } else if (cartItems.some(item => !isCheckoutItemValid(item))) {
            errors.cart = 'Retira los productos no disponibles antes de finalizar el pedido.';
        }

        if (name.trim().length < 3) {
            errors.name = 'Ingresa tu nombre completo.';
        }

        if (!/^3\d{9}$/.test(telefonoNormalizado)) {
            errors.telefono = 'Ingresa un celular válido de 10 dígitos.';
        }

        if (!deliveryOptions.includes(deliveryOption)) {
            errors.deliveryOption = 'Selecciona una opción de entrega válida.';
        }

        if (deliveryOption === 'delivery' && address.trim().length < 5) {
            errors.address = 'Ingresa la dirección de entrega.';
        }

        const paymentMethodIsActive = metodosStatus === 'loaded'
            && metodos.some(metodo => metodo.tipo === paymentMethod);
        if (!paymentMethodIsActive) {
            errors.paymentMethod = metodosStatus === 'loading'
                ? 'Espera a que terminen de cargar los métodos de pago.'
                : 'Selecciona un método de pago activo.';
        }

        return { errors, telefonoNormalizado };
    };

    //pedido-------------------------------------------------------------------
    const [mensaje, setMensaje] = useState('');
    const crearPedido = async () => {
        const { errors, telefonoNormalizado } = validateCheckout();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setMensaje('');
            return;
        }

        setFormErrors({});
        setMensaje('Procesando...');

        try {
            // Construir la lista de productos del pedido
            const productosPedido = cartItems
                ?.filter(isCheckoutItemValid)
                ?.map(item => ({
                    idProducto: item?.idProducto,
                    idCategoria: item.idCategoria,
                    titulo: item?.titulo,
                    cantidad: item?.cantidad,
                    items: item?.items,
                    precio: item?.precio,
                    imagen: obtenerImagen(item)
                }));

            // Convertir la lista de productos a JSON
            const productosPedidoJSON = JSON.stringify(productosPedido);

            // Calcular el precio total del pedido
            let totalPrice = 0;
            cartItems.filter(isCheckoutItemValid).forEach(item => {
                totalPrice += item?.precio * item?.cantidad;
            });

            // Enviar el pedido con el precio total descontado
            const formData = new FormData();
            formData.append('productos', productosPedidoJSON);
            formData.append('total', totalPrice);
            formData.append('nombre', name.trim());
            formData.append('telefono', telefonoNormalizado);

            const entregaPorOpcion = {
                delivery: address.trim(),
                pickup: 'Retiro en Sucursal',
                digital: 'Envío Digital/Online'
            };
            const entrega = entregaPorOpcion[deliveryOption] || '';
            formData.append('entrega', entrega);
            formData.append('pago', paymentMethod);
            formData.append('nota', noteText);
            formData.append('codigo', codigo);
            formData.append('estado', 'Pendiente');
            formData.append('pagado', 'No');
            formData.append('pagoRecibir', pagoRecibir);
            formData.append('createdAt', createdAt);

            const response = await fetch(`${baseURL}/pedidoPost.php`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();
            if (!response?.ok) {
                setMensaje('');
                Swal.fire({
                    title: 'No pudimos confirmar el pedido',
                    text: 'No fue posible completar la solicitud. Conserva tu carrito e inténtalo más tarde.',
                    icon: 'error',
                });
                return;
            }

            const pedidoConfirmado = obtenerPedidoConfirmado(data);
            if (!pedidoConfirmado) {
                setMensaje('');
                Swal.fire({
                    title: 'Pedido pendiente de verificación',
                    text: 'El servidor no devolvió todos los datos de seguimiento del pedido. No vuelvas a enviarlo hasta verificarlo con atención al cliente.',
                    icon: 'warning',
                });
                return;
            }

            setMensaje('');
            localStorage.setItem('idPedido', pedidoConfirmado.idPedido);
            localStorage.setItem('tokenConsulta', pedidoConfirmado.tokenConsulta);
            localStorage.setItem('consultaUrl', pedidoConfirmado.consultaUrl);
            setConsultaUrl(pedidoConfirmado.consultaUrl);

            const whatsappOpened = handleWhatsappMessage(pedidoConfirmado);
            const confirmationText = whatsappOpened
                ? `Pedido N.º ${pedidoConfirmado.idPedido} creado correctamente. Usa "Ver mi pedido" para consultar el seguimiento.`
                : `Pedido N.º ${pedidoConfirmado.idPedido} creado correctamente. Usa "Ver mi pedido" para consultar el seguimiento. No pudimos abrir WhatsApp.`;

            Swal.fire({
                title: 'Pedido enviado',
                text: confirmationText,
                icon: 'success',
            });

            setName('');
            setCodigo('');
            setNoteText('');
            closeModal();
            closeModal2();
            clearCart();
        } catch (error) {
            console.error('Error:', error);
            setMensaje('');
            Swal.fire(
                'Error',
                'Error de conexión. Por favor, inténtelo de nuevo.',
                'error'
            );
        }
    };

    const handlePaymentChange = (metodo) => {
        if (
            metodosStatus !== 'loaded'
            || !metodos.some(currentMethod => currentMethod.tipo === metodo)
        ) {
            return;
        }

        setPaymentMethod(metodo);
        if (metodo === 'Transferencia') {
            setPagoRecibir('Si'); // Valor predeterminado "Sí" para Transferencia
        } else {
            setPagoRecibir(''); // Borra el valor si no es Transferencia
        }
    };
    const handlePagoRecibirChange = (value) => {
        setPagoRecibir(value);
    };

    return (
        <div>
            <Modal
                isOpen={isOpen}
                className="modal-cart"
                overlayClassName="overlay-cart"
                onRequestClose={closeModal}
                onAfterClose={onAfterClose}
                shouldReturnFocusAfterClose
            >
                <div className='deFLex'>
                    <button onClick={closeModal} ><FontAwesomeIcon icon={faArrowLeft} />  </button>
                    <Anchor to="/mi-pedido" onClick={closeModal}>
                        Consultar mi pedido
                    </Anchor>
                    {consultaUrl && (
                        <a href={consultaUrl} target="_blank" rel="noopener noreferrer">
                            Ver mi pedido
                        </a>
                    )}
                </div>


                {
                    estado === 'Abierto' ? (
                        <>
                            {cartItems?.length === 0 ?
                                (<p className='nohay'> No hay productos</p>)
                                : (<>
                                    <div className="modal-content-cart">


                                        {loadingProductos ? (
                                             <p>Cargando...</p>
                                         ) : (
                                             <div>
                                                {catalogStatus.productos === 'error' && (
                                                    <div role="alert">
                                                        <p>No pudimos actualizar la información de los productos.</p>
                                                        <button type="button" onClick={retryCatalogProducts}>
                                                            Reintentar productos
                                                        </button>
                                                    </div>
                                                )}

                                                 {cartItems.map((item, index) => (
                                                     <div key={`${item?.idProducto || 'invalid'}-${item.cartIndex}`} className='cardProductCart' >
                                                         {item.productoResuelto ? (
                                                             <Anchor to={`/producto/${item?.idProducto}/${item?.titulo?.replace(/\s+/g, '-')}`} onClick={closeModal}>
                                                                 <img src={obtenerImagen(item)} alt={item.titulo} loading="lazy" decoding="async" />
                                                             </Anchor>
                                                         ) : (
                                                             obtenerImagen(item)
                                                                 ? <img src={obtenerImagen(item)} alt={item.titulo} loading="lazy" decoding="async" />
                                                                 : <span>Sin imagen disponible</span>
                                                         )}
                                                         <div className='cardProductCartText'>
                                                             <h3>{item.titulo}</h3>
                                                             {!item.productoResuelto && (
                                                                 <span role="status">{getUnavailableProductMessage(item)}</span>
                                                             )}
                                                             <span>
                                                                 {item?.items?.map((sabor, index) => (
                                                                     <span key={index}>{sabor}, </span>
                                                                 ))}
                                                             </span>
                                                             {item?.precio !== undefined && item?.precio !== null
                                                                 ? <strong>{moneda} {item.precio.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</strong>
                                                                 : <strong>Precio no disponible</strong>}
                                                         </div>
                                                         <div className='deColumn'>
                                                             <button
                                                                 type="button"
                                                                 aria-label={`Eliminar ${item.titulo}`}
                                                                 onClick={() => removeFromCart(index)}
                                                                 className='deleteCart'
                                                             >
                                                                 <FontAwesomeIcon icon={faTrash} />
                                                             </button>
                                                             <div className='deFlexCantidad'>
                                                                 <button type="button" aria-label={`Disminuir cantidad de ${item.titulo}`} onClick={() => decreaseQuantity(index)}>-</button>
                                                                 <span>{item.cantidad}</span>
                                                                 <button type="button" aria-label={`Aumentar cantidad de ${item.titulo}`} onClick={() => increaseQuantity(index)}>+</button>
                                                             </div>
                                                         </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                     </div>
                                     <div className='deColumnCart'>
                                         <h4>Total estimado: {moneda} {totalPrice?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h4>
                                         <p>
                                             Compra sin crear cuenta. Enviaremos tu pedido por WhatsApp para confirmar disponibilidad y entrega.
                                         </p>
                                         {hasUnresolvedItems && (
                                             <p role="alert">Retira los productos no disponibles antes de finalizar el pedido.</p>
                                         )}
                                        <div className='deFLexBtns'>
                                                <button className='btnWpp' onClick={openModal2}>
                                                    Pedir por WhatsApp<img src={whatsappIcon} alt="WhatsApp" />
                                                </button>
                                        </div>
                                    </div>

                                    <Modal
                                        isOpen={modalIsOpen2}
                                        onRequestClose={closeModal2}
                                        className="modal-cart"
                                        overlayClassName="overlay-cart"
                                    >
                                        <div className='deFLex'>
                                            <button onClick={closeModal2} ><FontAwesomeIcon icon={faArrowLeft} />  </button>
                                            <h4>(*) Campos obligatorios</h4>
                                         </div>
                                         <div className="modal-send-form">
                                             {catalogStatus.tienda === 'error' && (
                                                 <div role="alert">
                                                     <p>No pudimos cargar el contacto de WhatsApp. Puedes crear el pedido y conservar su seguimiento.</p>
                                                     <button type="button" onClick={retryCatalogStore}>
                                                         Reintentar contacto
                                                     </button>
                                                 </div>
                                             )}
                                             {Object.keys(formErrors).length > 0 && (
                                                <div
                                                    role="alert"
                                                    aria-live="assertive"
                                                    style={{
                                                        color: '#9f1c16',
                                                        backgroundColor: '#fff1f0',
                                                        border: '1px solid #f5b7b1',
                                                        borderRadius: '6px',
                                                        padding: '10px 12px'
                                                    }}
                                                >
                                                    <strong>Revisa los datos del pedido:</strong>
                                                    <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                                                        {Object.entries(formErrors).map(([field, error]) => (
                                                            <li key={field}>{error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <p>Usaremos tus datos solo para coordinar tu pedido.</p>
                                            <input
                                                type="text"
                                                id="name"
                                                value={name}
                                                onChange={(e) => {
                                                    setName(e.target.value);
                                                    clearFormError('name');
                                                }}
                                                aria-invalid={Boolean(formErrors.name)}
                                                placeholder='Apellido y Nombre (*)'
                                            />

                                            <input
                                                type="tel"
                                                id="telefono"
                                                value={telefono}
                                                onChange={(e) => {
                                                    setTelefono(e.target.value);
                                                    clearFormError('telefono');
                                                }}
                                                inputMode="tel"
                                                autoComplete="tel"
                                                aria-invalid={Boolean(formErrors.telefono)}
                                                placeholder='Telefono / WathsApp (*)'
                                            />
                                            <div className='deFLexRadio'>
                                                <label>Opciones de entrega (*)</label>

                                                <div
                                                    onClick={() => selectDeliveryOption('delivery')}
                                                >
                                                    <input
                                                        type="radio"
                                                        id="delivery"
                                                        name="deliveryOption"
                                                        value="delivery"
                                                        checked={deliveryOption === 'delivery'}
                                                        onChange={() => selectDeliveryOption('delivery')}
                                                    />
                                                    <label htmlFor="delivery">Envío a domicilio</label>
                                                </div>

                                                <div
                                                    onClick={() => selectDeliveryOption('pickup')}
                                                >
                                                    <input
                                                        type="radio"
                                                        id="pickup"
                                                        name="deliveryOption"
                                                        value="pickup"
                                                        checked={deliveryOption === 'pickup'}
                                                        onChange={() => selectDeliveryOption('pickup')}
                                                    />
                                                    <label htmlFor="pickup">Retiro en Sucursal</label>
                                                </div>

                                                <div
                                                    onClick={() => selectDeliveryOption('digital')}
                                                >
                                                    <input
                                                        type="radio"
                                                        id="digital"
                                                        name="deliveryOption"
                                                        value="digital"
                                                        checked={deliveryOption === 'digital'}
                                                        onChange={() => selectDeliveryOption('digital')}
                                                    />
                                                    <label htmlFor="digital">Envío Digital/Online</label>
                                                </div>


                                            </div>
                                            <p>
                                                Si no sabes qué producto elegir, escríbenos por WhatsApp y te asesoramos.
                                            </p>
                                            {deliveryOption === 'delivery' && (

                                                <input
                                                    type="text"
                                                    id="address"
                                                    name="address"
                                                    value={address}
                                                    onChange={(e) => {
                                                        setAddress(e.target.value);
                                                        clearFormError('address');
                                                    }}
                                                    aria-invalid={Boolean(formErrors.address)}
                                                    placeholder="Dirección / Nro. (*)"
                                                />

                                            )}
                                            <div className='deFLexRadio'>
                                                <label>Formas de pago (*)</label>
                                                {(metodosStatus === 'idle' || metodosStatus === 'loading') && (
                                                    <p role="status">Actualizando métodos de pago...</p>
                                                )}
                                                {metodosStatus === 'error' && (
                                                    <div role="alert">
                                                        <p>No pudimos cargar los métodos de pago.</p>
                                                        <button type="button" onClick={retryPaymentMethods}>
                                                            Reintentar métodos
                                                        </button>
                                                    </div>
                                                )}
                                                {metodosStatus === 'loaded' && metodos.length === 0 && (
                                                    <p role="status">No hay métodos de pago activos disponibles.</p>
                                                )}
                                                {metodosStatus === 'loaded' && metodos.map(metodo => (
                                                    <section key={metodo.idMetodo} className='columnRadio'>
                                                        <div>
                                                            <input
                                                                type="radio"
                                                                name="paymentMethod"
                                                                id={metodo.tipo}
                                                                value={metodo.tipo}
                                                                checked={paymentMethod === metodo.tipo}
                                                                onChange={() => handlePaymentChange(metodo.tipo)}
                                                            />
                                                            <label htmlFor={metodo.tipo}>{metodo.tipo}</label>
                                                        </div>

                                                        {paymentMethod === 'Transferencia' && metodo.tipo === 'Transferencia' && (
                                                            <section className='radioRecibi'>
                                                                <label htmlFor="">Pago al recibirlo</label>
                                                                <div>
                                                                    <input
                                                                        type="radio"
                                                                        name="pagoRecibir"
                                                                        id="Sí"
                                                                        value="Si"
                                                                        checked={pagoRecibir === 'Si'}
                                                                        onChange={() => handlePagoRecibirChange('Si')}
                                                                    />
                                                                    <label htmlFor="Sí">Sí</label>
                                                                </div>
                                                                <div>
                                                                    <input
                                                                        type="radio"
                                                                        name="pagoRecibir"
                                                                        id="No"
                                                                        value="No"
                                                                        checked={pagoRecibir === 'No'}
                                                                        onChange={() => handlePagoRecibirChange('No')}
                                                                    />
                                                                    <label htmlFor="No">No (envío comprobante)</label>
                                                                </div>
                                                            </section>
                                                        )}
                                                    </section>
                                                ))}


                                            </div>
                                            {metodosStatus === 'loaded' && metodos.some(m => m.tipo === paymentMethod) ? (
                                                <>
                                                    {metodos
                                                        ?.filter(metodo => metodo.tipo === paymentMethod)
                                                        ?.map(datos => datos?.datos === '' ? null : (
                                                            <div key={datos.idDatos || datos.idMetodo} className='deFLexRadioMetod'>
                                                                <span>{datos.datos}</span>
                                                            </div>
                                                        ))}
                                                </>
                                            ) : null}



                                            <input
                                                type="text"
                                                id="codigo"
                                                value={codigo}
                                                onChange={(e) => {
                                                    setCodigo(e.target.value);
                                                    resetCodigoValidacion();
                                                }}
                                                onBlur={validarCodigoPublico}
                                                placeholder='Codigo de descuento (opcional)'
                                            />
                                            {validandoCodigo && (
                                                <p role="status">Validando codigo...</p>
                                            )}
                                            {!validandoCodigo && codigoMensaje && (
                                                <p
                                                    role="status"
                                                    style={{
                                                        color: codigoValido ? '#063f4f' : '#9f1c16',
                                                        fontWeight: codigoValido ? 600 : 500,
                                                        margin: '0'
                                                    }}
                                                >
                                                    {codigoMensaje}
                                                    {codigoValido && descuento > 0
                                                        ? ` Descuento estimado: ${moneda} ${descuento.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}.`
                                                        : ''}
                                                </p>
                                            )}
                                            <textarea
                                                placeholder="Agrega aquí detalles o solicitudes del pedido (Opcional)"
                                                value={noteText}
                                                onChange={(e) => setNoteText(e.target.value)}
                                            />
                                            <fieldset className='deNonefieldset'>
                                                <legend>Productos</legend>
                                                <textarea
                                                    name='productos'
                                                    value={cartItems.map(item => `${item.titulo}, x ${item.cantidad}, ${item.items}, ${item.precio}, ${obtenerImagen(item)}  `).join('\n')}
                                                    readOnly
                                                />
                                            </fieldset>


                                        </div>
                                            {mensaje ? (
                                                <button type='button' className='btn' disabled>{mensaje}</button>
                                            ) : (
                                                <button
                                                    type='button'
                                                    onClick={crearPedido}
                                                    className='btn'
                                                    disabled={
                                                        metodosStatus !== 'loaded'
                                                        || !metodos.some(metodo => metodo.tipo === paymentMethod)
                                                    }
                                                >
                                                    Finalizar pedido<img src={whatsappIcon} alt="WhatsApp" />
                                             </button>
                                        )}
                                   </Modal>

                                </>)}
                        </>
                    ) : (
                        <p className='nohay'>El establecimiento se encuentra <br />cerrado en estos momentos. <br />Consulte por horarios de atención.</p>
                    )
                }

            </Modal>
        </div >
    );
}
